import React, { useEffect, useState } from 'react';
import { Form, Modal, Button, Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { ContactType, RelationshipTypeToText, RoleTypeToText, IContact } from '../../../models';
import { DateInput, DescriptionItem, EmailInput, GenderInput, IdInput, PhoneInput, SwitchInput, TextInput } from '../../atom/form';
import { useScreenSize, useCurrentOrganization } from '../../../hooks';
import { ModalState, renderActionButtons, NewDeleteModal } from '../crud';
import { ContactTypeRadio, RelationshipTypeDropdown, RoleTypeDropdown } from '.';
import { StateDropdown } from '../states/states-dropdown';
import { useContactsContext } from './contacts-context';

type NewContactModalProps = {
  onSave?: (obj: IContact) => void;
};

export function NewContactModal(props?: NewContactModalProps) {
  const isSmallScreen = useScreenSize();

  const contactsContext = useContactsContext();

  const currentOrganization = useCurrentOrganization();
  const [contactType, setContactType] = useState<ContactType>();
  const [hasOrganisationContact, setHasOrganisationContact] = useState<boolean>();
  const [hasGuardian, setHasGuardian] = useState<boolean>();

  const isClient = contactType === ContactType.Client;
  const isOrg = contactType === ContactType.Organisation;

  useEffect(
    function onLoad() {
      const value = {
        ...contactsContext.contactActions.modal.obj,
        invoices: contactsContext.contactActions.modal.obj?.invoices || [],
        contactType: (contactsContext.contactActions.modal.obj?.contactType as ContactType) ?? ContactType.Client,
        hasGuardian: contactsContext.contactActions.modal.obj?.hasGuardian ?? false,
        hasOrganisationContact: contactsContext.contactActions.modal.obj?.hasOrganisationContact ?? false,
      };

      setContactType(value.contactType);
      setHasGuardian(value.hasGuardian);
      setHasOrganisationContact(value.hasOrganisationContact);

      contactsContext.contactActions.save.hook.form.resetFields();
      contactsContext.contactActions.save.hook.form.setFieldsValue(value);
    },
    [contactsContext.contactActions.modal.modalState, contactsContext.contactActions.modal.obj],
  );

  const renderViewMode = () => {
    return (
      <>
        <DescriptionItem title="Contact type" content={contactsContext.contactActions.modal.obj?.contactType} />
        <p className="modal-subtitle" style={{ marginTop: 0 }}>
          Basic information
        </p>
        {isClient && (
          <>
            <DescriptionItem title="Firstname" content={contactsContext.contactActions.modal.obj?.firstName} />
            <DescriptionItem title="Surname" content={contactsContext.contactActions.modal.obj?.lastName} />
            <DescriptionItem title="NDIS number" content={contactsContext.contactActions.modal.obj?.ndisNumber} />
            <DescriptionItem title="Gender" content={contactsContext.contactActions.modal.obj?.gender} />
            <DescriptionItem title="Mobile number" content={contactsContext.contactActions.modal.obj?.mobileNumber} />
          </>
        )}
        {isOrg && (
          <>
            <DescriptionItem title="Name" content={contactsContext.contactActions.modal.obj?.organisationName} />
          </>
        )}

        <p className="modal-subtitle">Contact information</p>
        <DescriptionItem title="Email" content={contactsContext.contactActions.modal.obj?.email} />
        <DescriptionItem title="Address Line 1" content={contactsContext.contactActions.modal.obj?.addressLine1} />
        <DescriptionItem title="Address Line 2" content={contactsContext.contactActions.modal.obj?.addressLine2} />
        <DescriptionItem title="State" content={contactsContext.contactActions.modal.obj?.state?.name} />
        <DescriptionItem title="Postcode" content={contactsContext.contactActions.modal.obj?.postcode} />

        {hasGuardian && (
          <>
            <p className="modal-subtitle">Additional contact</p>
            <DescriptionItem title="Name" content={contactsContext.contactActions.modal.obj?.guardianFullName} />
            <DescriptionItem title="Email" content={contactsContext.contactActions.modal.obj?.guardianEmail} />
            <DescriptionItem title="Phone number" content={contactsContext.contactActions.modal.obj?.guardianPhoneNumber} />
            <DescriptionItem
              title="Relationship"
              content={contactsContext.contactActions.modal.obj?.guardianRelationshipType ? RelationshipTypeToText(contactsContext.contactActions.modal.obj.guardianRelationshipType) : ''}
            />
          </>
        )}

        {hasOrganisationContact && (
          <>
            <p className="modal-subtitle">Additional contact</p>
            <DescriptionItem title="Name" content={contactsContext.contactActions.modal.obj?.organisationContactName} />
            <DescriptionItem title="Email" content={contactsContext.contactActions.modal.obj?.organisationContactMobileNumber} />
            <DescriptionItem title="Phone number" content={contactsContext.contactActions.modal.obj?.organisationContactEmail} />
          </>
        )}

        {contactsContext.contactActions.modal.obj?.invoices && contactsContext.contactActions.modal.obj.invoices.length > 0 && (
          <>
            <p className="modal-subtitle">Who will receive the invoices</p>
            {contactsContext.contactActions.modal.obj?.invoices.map((item, index) => {
              return (
                <React.Fragment key={`receive-invoice-${index}`}>
                  <DescriptionItem title="Email" content={item.email} />
                  <DescriptionItem title="Role" content={RoleTypeToText(item.roleType)} />
                </React.Fragment>
              );
            })}
          </>
        )}
      </>
    );
  };

  const renderEditMode = () => {
    return (
      <>
        <Form form={contactsContext.contactActions.save.hook.form} layout="vertical">
          <IdInput />
          <ContactTypeRadio
            name="contactType"
            label="Contact type"
            onChange={(item) => {
              setContactType(item);
            }}
            required
            disabled={contactsContext.contactActions.save.hook.isSaving}
          />

          <p className="modal-subtitle">Basic information</p>
          {isClient && (
            <>
              <TextInput name="firstName" label="First name" required disabled={contactsContext.contactActions.save.hook.isSaving} />
              <TextInput name="lastName" label="Surname" required disabled={contactsContext.contactActions.save.hook.isSaving} />
              <TextInput name="ndisNumber" label="NDIS Number" disabled={contactsContext.contactActions.save.hook.isSaving} />
              <GenderInput name="gender" label="Gender" disabled={contactsContext.contactActions.save.hook.isSaving} />
              <DateInput name="dob" label="Date of Birth" disabled={contactsContext.contactActions.save.hook.isSaving} />
              <PhoneInput name="mobileNumber" label="Mobile number" disabled={contactsContext.contactActions.save.hook.isSaving} />
            </>
          )}
          {isOrg && (
            <>
              <TextInput name="organisationName" label="Name" required disabled={contactsContext.contactActions.save.hook.isSaving} />
            </>
          )}

          <p className="modal-subtitle">Contact information</p>
          <EmailInput name="email" label="Email" disabled={contactsContext.contactActions.save.hook.isSaving} />
          <TextInput name="addressLine1" label="Address Line 1" disabled={contactsContext.contactActions.save.hook.isSaving} />
          <TextInput name="addressLine2" label="Address Line 2" disabled={contactsContext.contactActions.save.hook.isSaving} />
          <StateDropdown name="idState" idCountry={currentOrganization.country.id} hideAll disabled={contactsContext.contactActions.save.hook.isSaving} />
          <TextInput name="postcode" label="Postcode" disabled={contactsContext.contactActions.save.hook.isSaving} />
          {isClient && (
            <>
              <SwitchInput
                name="hasGuardian"
                label="Do you wish to add another contact for your Client?"
                checked={hasGuardian}
                onChange={(hasGuardian) => setHasGuardian(hasGuardian)}
                disabled={contactsContext.contactActions.save.hook.isSaving}
              />
              {hasGuardian && (
                <>
                  <TextInput name="guardianFirstName" label="First name" required disabled={contactsContext.contactActions.save.hook.isSaving} />
                  <TextInput name="guardianLastName" label="Surname" required disabled={contactsContext.contactActions.save.hook.isSaving} />
                  <EmailInput name="guardianEmail" label="Email" disabled={contactsContext.contactActions.save.hook.isSaving} />
                  <PhoneInput name="guardianPhoneNumber" label="Phone number" disabled={contactsContext.contactActions.save.hook.isSaving} />
                  <RelationshipTypeDropdown name="guardianRelationshipType" label="Relationship" disabled={contactsContext.contactActions.save.hook.isSaving} />
                </>
              )}
            </>
          )}
          {isOrg && (
            <>
              <SwitchInput
                name="hasOrganisationContact"
                label="Does the Organisation have a contact?"
                checked={hasOrganisationContact}
                onChange={(hasOrganisationContact) => setHasOrganisationContact(hasOrganisationContact)}
                disabled={contactsContext.contactActions.save.hook.isSaving}
              />
              {hasOrganisationContact && (
                <>
                  <TextInput name="organisationContactName" label="Name" required disabled={contactsContext.contactActions.save.hook.isSaving} />
                  <PhoneInput name="organisationContactMobileNumber" label="Mobile number" disabled={contactsContext.contactActions.save.hook.isSaving} />
                  <EmailInput name="organisationContactEmail" label="Email" disabled={contactsContext.contactActions.save.hook.isSaving} />
                </>
              )}
            </>
          )}

          {isClient && (
            <>
              {/* TODO: This could be its own component */}
              <p className="modal-subtitle">Who will receive the invoices?</p>
              <Form.List name="invoices">
                {(list, { add, remove }) => (
                  <>
                    {list &&
                      list.map((i, index) => (
                        <div key={i.name}>
                          <EmailInput name={[i.name, 'email']} label="Email" required disabled={contactsContext.contactActions.save.hook.isSaving} />
                          <RoleTypeDropdown name={[i.name, 'roleType']} label="Role" required disabled={contactsContext.contactActions.save.hook.isSaving} />
                          <Button type="dashed" danger style={{ marginBottom: '10px', width: '100%' }} onClick={() => remove(index)} disabled={contactsContext.contactActions.save.hook.isSaving}>
                            Remove recipient
                          </Button>
                        </div>
                      ))}
                    <Button type="primary" block style={{ marginBottom: '10px', width: '100%' }} onClick={() => add()} id="add-recipient" disabled={contactsContext.contactActions.save.hook.isSaving}>
                      <PlusOutlined />
                      Add recipient
                    </Button>
                  </>
                )}
              </Form.List>
            </>
          )}
        </Form>
      </>
    );
  };

  return (
    <>
      <Modal
        title={`${contactsContext.contactActions.modal.modalState} contact`}
        visible={contactsContext.contactActions.modal.modalState !== ModalState.Hidden}
        onCancel={contactsContext.contactActions.modal.closeModal}
        footer={
          <Row justify="space-between">
            <Row>
              <Button onClick={contactsContext.contactActions.modal.closeModal}>Close</Button>
            </Row>
            <Row>
              {renderActionButtons(contactsContext.contactActions.modal.obj!, [
                contactsContext.contactActions.deleteContact.definition as any,
                contactsContext.contactActions.editContact.definition as any,
                contactsContext.contactActions.save.getDefinition(props?.onSave) as any,
              ])}
            </Row>
          </Row>
        }
        width={720}
        wrapClassName={isSmallScreen ? 'fullscreen' : ''}
        className={contactsContext.contactActions.modal.modalState === ModalState.View ? 'read-mode' : ''}
      >
        {contactsContext.contactActions.modal.modalState === ModalState.View && renderViewMode()}
        {(contactsContext.contactActions.modal.modalState === ModalState.New || contactsContext.contactActions.modal.modalState === ModalState.Edit) && renderEditMode()}
      </Modal>
      <NewDeleteModal
        deleting={contactsContext.contactActions.deleteContact.hook.isDeleting}
        obj={contactsContext.contactActions.deleteContact.hook.obj}
        onOk={contactsContext.contactActions.deleteContact.handleOk}
        onCancel={contactsContext.contactActions.deleteContact.handleCancel}
      >
        Are you sure you want to delete contact "<span style={{ fontWeight: 'bold' }}>{contactsContext.contactActions.deleteContact.hook.obj?.fullName}</span>"?
      </NewDeleteModal>
    </>
  );
}
